import {
  SmartAutoComplete,
  SmartForm,
  SmartTextField,
} from "@nodeon/react-components";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Coordinate,
  MapAreaTypeEnum,
  useMapAreaApi,
  usePortApi,
} from "~/api/GeneratedApi";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

export interface PortFormInputs {
  name: string;
  description: string;
  locodeId: number | null;
  mapAreaIds: number[];
}

interface Props {
  onSubmit: (data: PortFormInputs) => void;
  readOnly?: boolean;
  defaultValues?: PortFormInputs;
  center?: Coordinate;
  small?: boolean;
}

const PortForm = ({
  onSubmit,
  readOnly,
  defaultValues,
  small = false,
  center = { x: 0, y: 0 },
}: Props): JSX.Element => {
  const { t } = useTranslation("portDialog");

  const mapAreaApi = useMapAreaApi();
  const portApi = usePortApi();

  const { isLoading: isLoadingAreas, data: mapAreas } = useCustomQuery(
    "map-areas",
    () => mapAreaApi.getMapAreas(),
  );

  const { isLoading: isLoadingLocodes, data: portLocodes } = useCustomQuery(
    "port-locodes",
    () => portApi.getLocodes(center),
  );

  const formDefaultValues = useMemo(
    () =>
      defaultValues ?? {
        name: "",
        description: "",
        locodeId: null,
        mapAreaIds: [],
      },
    [defaultValues],
  );

  return (
    <SmartForm<PortFormInputs>
      onSubmit={onSubmit}
      gridProps={{ justifyContent: "center" }}
      formProps={{
        defaultValues: formDefaultValues,
      }}
      formId="addPortForm"
    >
      <SmartTextField<PortFormInputs>
        name="name"
        label={t("name")}
        gridProps={{ xs: 8, lg: small ? 5 : 4 }}
        required
        fullWidth
        autoComplete="off"
        InputProps={{ readOnly }}
        disabled={readOnly}
        autoFocus
      />
      <SmartTextField<PortFormInputs>
        name="description"
        label={t("description")}
        gridProps={{ xs: 8, lg: small ? 5 : 4 }}
        fullWidth
        autoComplete="off"
        InputProps={{ readOnly }}
        disabled={readOnly}
      />
      <SmartAutoComplete<PortFormInputs, number, undefined, undefined, false>
        name="locodeId"
        label={t("portManagement:locode")}
        tooltip={t("portManagement:locode-tooltip").toString()}
        options={portLocodes?.map((x) => x.id) ?? []}
        gridProps={{ xs: small ? 11 : 12 }}
        fullWidth
        disabled={isLoadingLocodes || readOnly}
        controllerProps={{
          rules: {
            required: { value: true, message: t("form:required") },
          },
        }}
        getOptionLabel={(opt) => {
          if (!(typeof opt === "number")) return "";
          const locode = portLocodes?.find((x) => x.id === opt);
          if (!locode) return t("general:unknown");
          // Just the locode, or locode + portArea details if they exist
          return `${locode.locode}${
            locode.portAreaCode && locode.portAreaName
              ? ` - ${locode.portAreaName} - ${locode.portAreaCode}`
              : ""
          }`;
        }}
      />

      <SmartAutoComplete<PortFormInputs, number, true, undefined, false>
        name="mapAreaIds"
        label={t("portManagement:map-areas")}
        options={mapAreas?.results.map((x) => x.mapAreaId) ?? []}
        gridProps={{ xs: small ? 11 : 12 }}
        fullWidth
        multiple
        disabled={isLoadingAreas || readOnly}
        // controllerProps={{
        //   rules: {
        //     required: { value: true, message: t("form:required") },0
        //   },
        // }}
        getOptionLabel={(opt) => {
          if (!(typeof opt === "number")) return "";
          const ma = mapAreas?.results.find((x) => x.mapAreaId === opt);
          if (!ma) return t("general:unknown");
          return ma.mapAreaType === MapAreaTypeEnum.Project
            ? `${ma.projectID ? `${ma.projectID} -` : ""} ${ma.name}`
            : ma.name;
        }}
      />
    </SmartForm>
  );
};

export default PortForm;
